<template>
  <div class="pt-detail-page">
    <header class="pt-detail-page__header">
      <button class="pt-detail-page__back" @click="goBack">←</button>
      <div class="pt-detail-page__title-block">
        <h2 class="pt-detail-page__title">{{ session && session.title ? session.title : ($t ? $t('(제목 없음)') : '(제목 없음)') }}</h2>
        <div v-if="session" class="pt-detail-page__sub">
          <span>{{ formatTime(session.started_at) }}</span>
          <span class="pt-detail-page__dot">·</span>
          <span>{{ formatDuration(session) }}</span>
          <span class="pt-detail-page__dot">·</span>
          <span>{{ session.turn_count }} {{ $t ? $t('발화') : '발화' }}</span>
          <span class="pt-detail-page__dot">·</span>
          <span class="pt-detail-page__mode" :class="'pt-detail-page__mode--' + session.mode">{{ session.mode === 'live' ? 'LIVE' : 'STT' }}</span>
        </div>
      </div>
      <div class="pt-detail-page__actions">
        <button class="pt-detail-page__btn" @click="exportTxt">⤓ TXT</button>
        <button class="pt-detail-page__btn" @click="exportCsv">⤓ CSV</button>
        <button class="pt-detail-page__btn" @click="copyAll">⧉ {{ $t ? $t('전체 복사') : '전체 복사' }}</button>
      </div>
    </header>

    <div class="pt-detail-page__body">
      <div v-if="loading" class="pt-detail-page__state">{{ $t ? $t('불러오는 중…') : '불러오는 중…' }}</div>
      <div v-else-if="error" class="pt-detail-page__state pt-detail-page__state--err">{{ error }}</div>
      <div v-else-if="!turns.length" class="pt-detail-page__state">
        {{ $t ? $t('이 세션에는 기록된 발화가 없습니다.') : '이 세션에는 기록된 발화가 없습니다.' }}
      </div>

      <ul v-else class="pt-detail-turns">
        <li v-for="turn in turns" :key="turn.id" class="pt-detail-turn">
          <div class="pt-detail-turn__head">
            <span class="pt-detail-turn__ord">#{{ turn.ord }}</span>
            <span class="pt-detail-turn__time">{{ formatShortTime(turn.spoken_at) }}</span>
            <span v-if="turn.status !== 'success'" class="pt-detail-turn__status" :class="'pt-detail-turn__status--' + turn.status">
              {{ statusLabel(turn.status) }}
            </span>
            <button class="pt-detail-turn__copy" @click="copyTurn(turn)" :title="$t ? $t('복사') : '복사'">⧉</button>
          </div>
          <div class="pt-detail-turn__original">
            <span class="pt-detail-turn__lang">{{ turn.source_language }}</span>
            <span class="pt-detail-turn__text">{{ turn.original }}</span>
          </div>
          <div class="pt-detail-turn__translations">
            <div
              v-for="(text, lang) in turn.translations"
              :key="turn.id + '_' + lang"
              class="pt-detail-turn__translation"
            >
              <span class="pt-detail-turn__lang pt-detail-turn__lang--target">{{ lang }}</span>
              <span v-if="text" class="pt-detail-turn__text">{{ text }}</span>
              <span v-else class="pt-detail-turn__failed">{{ $t ? $t('(번역 실패)') : '(번역 실패)' }}</span>
              <span
                v-if="turn.model_used && turn.model_used[lang]"
                class="pt-detail-turn__model"
              >{{ turn.model_used[lang] }}</span>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { useVoicePtBroadcast } from '@/composables/useVoicePtBroadcast'

const STATUS_LABEL = {
  success: '성공',
  translation_partial: '일부 번역 실패',
  translation_failed: '번역 실패',
  stt_failed: 'STT 실패',
}

export default {
  name: 'AiVoicePtHistoryDetail',
  data() {
    return {
      pt: null,
      session: null,
      turns: [],
      loading: false,
      error: null,
    }
  },
  computed: {
    projectId() { return this.$route.params.proj_id },
    sessionId() { return this.$route.params.sessionId },
  },
  created() {
    this.pt = useVoicePtBroadcast()
    this.loadAll()
  },
  methods: {
    async loadAll() {
      this.loading = true
      this.error = null
      try {
        const s = await this.pt.fetchSessionDetail(this.sessionId)
        this.session = s
        const t = await this.pt.fetchSessionTurns(this.sessionId, { limit: 2000, offset: 0 })
        if (t && Array.isArray(t.items)) this.turns = t.items
        else this.turns = []
      } catch (e) {
        this.error = (e && e.message) ? e.message : '상세 조회 실패'
      } finally {
        this.loading = false
      }
    },

    formatTime(iso) {
      if (!iso) return '-'
      try {
        const d = new Date(iso)
        const pad = function (n) { return n < 10 ? '0' + n : '' + n }
        return d.getFullYear() + '-' + pad(d.getMonth()+1) + '-' + pad(d.getDate())
          + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
      } catch (e) { return iso }
    },
    formatShortTime(iso) {
      if (!iso) return ''
      try {
        const d = new Date(iso)
        const pad = function (n) { return n < 10 ? '0' + n : '' + n }
        return pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds())
      } catch (e) { return '' }
    },
    formatDuration(s) {
      let sec = 0
      if (s.total_audio_seconds && s.total_audio_seconds > 0) {
        sec = Math.round(s.total_audio_seconds)
      } else if (s.started_at && s.ended_at) {
        try { sec = Math.max(0, Math.round((new Date(s.ended_at).getTime() - new Date(s.started_at).getTime()) / 1000)) } catch (e) { sec = 0 }
      }
      const m = Math.floor(sec / 60)
      const r = sec % 60
      return m + '분 ' + r + '초'
    },
    statusLabel(s) { return STATUS_LABEL[s] ? STATUS_LABEL[s] : s },

    goBack() {
      this.$router.push({
        name: 'ai-voice-pt-history',
        params: { proj_id: this.projectId },
      })
    },

    // ─── 복사 / 내보내기 ──────────────────────────────────
    _buildTurnText(turn) {
      const lines = []
      lines.push('[#' + turn.ord + ' ' + this.formatShortTime(turn.spoken_at) + ']')
      lines.push('  (' + turn.source_language + ') ' + (turn.original || ''))
      const keys = Object.keys(turn.translations || {})
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i]
        const v = turn.translations[k]
        lines.push('  (' + k + ') ' + (v ? v : '[FAILED]'))
      }
      return lines.join('\n')
    },

    async copyTurn(turn) {
      const txt = this._buildTurnText(turn)
      await this._writeClipboard(txt)
      this._notify('복사됨')
    },

    async copyAll() {
      const parts = []
      if (this.session && this.session.title) parts.push('# ' + this.session.title)
      parts.push('# ' + this.formatTime(this.session ? this.session.started_at : null))
      parts.push('')
      for (let i = 0; i < this.turns.length; i++) {
        parts.push(this._buildTurnText(this.turns[i]))
        parts.push('')
      }
      await this._writeClipboard(parts.join('\n'))
      this._notify('전체 복사됨')
    },

    exportTxt() {
      const parts = []
      if (this.session && this.session.title) parts.push('# ' + this.session.title)
      if (this.session) parts.push('# ' + this.formatTime(this.session.started_at))
      parts.push('')
      for (let i = 0; i < this.turns.length; i++) {
        parts.push(this._buildTurnText(this.turns[i]))
        parts.push('')
      }
      this._download((this.session && this.session.title ? this.session.title : 'pt-session') + '.txt', parts.join('\n'), 'text/plain')
    },

    exportCsv() {
      // 컬럼: ord, time, source_lang, original, lang1, text1, lang2, text2, ...
      // target_lang 별로 컬럼이 동적이므로, 모든 turn의 lang을 합집합으로 컬럼 결정.
      const langSet = {}
      for (let i = 0; i < this.turns.length; i++) {
        const ts = this.turns[i].translations || {}
        const keys = Object.keys(ts)
        for (let j = 0; j < keys.length; j++) langSet[keys[j]] = true
      }
      const langs = Object.keys(langSet)

      const esc = function (s) {
        if (s === null || s === undefined) return ''
        const str = String(s).replace(/"/g, '""')
        return '"' + str + '"'
      }

      const header = ['ord', 'time', 'source_lang', 'original'].concat(langs)
      const rows = [header.map(esc).join(',')]
      for (let i = 0; i < this.turns.length; i++) {
        const t = this.turns[i]
        const row = [t.ord, this.formatShortTime(t.spoken_at), t.source_language, t.original || '']
        for (let j = 0; j < langs.length; j++) {
          const v = (t.translations && t.translations[langs[j]]) ? t.translations[langs[j]] : ''
          row.push(v)
        }
        rows.push(row.map(esc).join(','))
      }
      const csv = '﻿' + rows.join('\n')  // BOM for Excel UTF-8
      this._download((this.session && this.session.title ? this.session.title : 'pt-session') + '.csv', csv, 'text/csv;charset=utf-8')
    },

    async _writeClipboard(text) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
          return
        }
      } catch (e) { /* fallthrough */ }
      // fallback
      const ta = document.createElement('textarea')
      ta.value = text
      document.body.appendChild(ta)
      ta.select()
      try { document.execCommand('copy') } catch (e) { /* ignore */ }
      document.body.removeChild(ta)
    },

    _download(filename, content, mime) {
      const blob = new Blob([content], { type: mime })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(function () { URL.revokeObjectURL(url) }, 1000)
    },

    _notify(msg) {
      if (this.$vs && this.$vs.notify) {
        this.$vs.notify({ title: 'PT', text: msg, color: 'success', position: 'top-center', time: 1500 })
      }
    },
  },
}
</script>

<style scoped>
.pt-detail-page {
  min-height: 100vh;
  background: #F8FAFC;
}

.pt-detail-page__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #E5E7EB;
  position: sticky;
  top: 0;
  z-index: 10;
}

.pt-detail-page__back {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  font-size: 20px;
  cursor: pointer;
  color: #374151;
  border-radius: 8px;
}
.pt-detail-page__back:hover { background: #F3F4F6; }

.pt-detail-page__title-block { flex: 1; min-width: 0; }
.pt-detail-page__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pt-detail-page__sub {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6B7280;
  margin-top: 2px;
}
.pt-detail-page__dot { color: #D1D5DB; }
.pt-detail-page__mode {
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 3px;
  letter-spacing: 0.5px;
  font-weight: 700;
}
.pt-detail-page__mode--live { background: #FEE2E2; color: #991B1B; }
.pt-detail-page__mode--stt  { background: #DBEAFE; color: #1E40AF; }

.pt-detail-page__actions { display: flex; gap: 6px; }
.pt-detail-page__btn {
  padding: 6px 12px;
  border: 1px solid #E5E7EB;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: #374151;
}
.pt-detail-page__btn:hover { background: #F3F4F6; }

.pt-detail-page__body { max-width: 920px; margin: 0 auto; padding: 24px; }
.pt-detail-page__state { padding: 48px 0; text-align: center; color: #6B7280; }
.pt-detail-page__state--err { color: #DC2626; }

.pt-detail-turns { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }

.pt-detail-turn {
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 12px 14px;
}

.pt-detail-turn__head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6B7280;
  margin-bottom: 8px;
}
.pt-detail-turn__ord { font-weight: 700; color: #4338CA; }
.pt-detail-turn__time { font-family: ui-monospace, monospace; }
.pt-detail-turn__status {
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 3px;
}
.pt-detail-turn__status--translation_partial { background: #FEF3C7; color: #92400E; }
.pt-detail-turn__status--translation_failed,
.pt-detail-turn__status--stt_failed { background: #FEE2E2; color: #991B1B; }
.pt-detail-turn__copy {
  margin-left: auto;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  color: #9CA3AF;
}
.pt-detail-turn__copy:hover { color: #374151; }

.pt-detail-turn__original {
  display: flex;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #F3F4F6;
  margin-bottom: 6px;
}

.pt-detail-turn__lang {
  display: inline-block;
  flex-shrink: 0;
  background: #1F2937;
  color: #fff;
  padding: 1px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  height: 18px;
  line-height: 18px;
}
.pt-detail-turn__lang--target { background: #E5E7EB; color: #374151; }

.pt-detail-turn__text { flex: 1; font-size: 14px; color: #1F2937; line-height: 1.5; }
.pt-detail-turn__failed { flex: 1; font-size: 13px; color: #9CA3AF; font-style: italic; }

.pt-detail-turn__translations { display: flex; flex-direction: column; gap: 4px; }
.pt-detail-turn__translation {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.pt-detail-turn__model {
  flex-shrink: 0;
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 3px;
  background: #F3F4F6;
  color: #6B7280;
  height: 18px;
  line-height: 18px;
}
</style>
